import {
  Alert,
  Anchor,
  Badge,
  Button,
  Checkbox,
  CopyButton,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { IconCopy, IconLock, IconLogin2, IconPlus, IconRefresh, IconSearch } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { datum, denar, stevilo, tocke } from '../oblika/oblika';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { useSeja } from '../seja/useSeja';
import { GlavaZaslona, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { useSeznam } from '../seznam/useSeznam';
import { NiNajdeno } from '../strani/NiNajdeno';
import { Bonusi } from './Bonusi';
import { Nakazila } from './Nakazila';
import { ZnackaNarocnine } from '../narocnina/ZnackaNarocnine';
import { EpostaSkrbnistvo } from './EpostaSkrbnistvo';
import { OkroznicaSkrbnistvo } from './OkroznicaSkrbnistvo';
import { PlacilaSkrbnistvo } from './PlacilaSkrbnistvo';
import { PovratneSkrbnistvo } from './PovratneSkrbnistvo';
import { AsistentSkrbnistvo } from './AsistentSkrbnistvo';
import { useNoveZahteveAsistenta } from './ai';
import { useNovePovratne } from '../povratne/povratne';
import { UmetnaInteligencaSkrbnistvo } from './UmetnaInteligencaSkrbnistvo';
import { Vstop } from './Vstop';
import { cas, predlogDomene, razvrstiInstance, type RazvrscanjeInstanc } from './skrbnistvo';
import { usePrijavaKot } from './prijavaKot';
import { glave, obNapaki, skrbniskiZeton, useSkrbniskiZeton } from './zeton';
import { NAZIVI_PAKETOV, PAKETI, type Paket } from '../seja/paket';

type Instanca = Shema['InstancaSkrbnik'];

/** Stanje instance kot oznake (aktivna/izklopljena, omejena). */
export function StanjeInstance({ i }: { i: Instanca }) {
  return (
    <Group gap={4} wrap="nowrap">
      {i.aktivna ? (
        <Badge color="green" variant="light">
          aktivna
        </Badge>
      ) : (
        <Badge color="gray" variant="filled">
          izklopljena
        </Badge>
      )}
      {!i.vseFunkcionalnosti && (
        <Badge color="orange" variant="light">
          omejena
        </Badge>
      )}
    </Group>
  );
}

/** Ovoj skrbniških zaslonov: ne-skrbnik vidi »ne obstaja«, brez skrbniške seje vnos kode. */
export function SkrbniskiZaslon({ children }: { children: React.ReactNode }) {
  const stanje = useSeja();
  const zeton = useSkrbniskiZeton();
  if (stanje.status !== 'prijavljen' || !stanje.skrbnik) {
    return <NiNajdeno />;
  }
  if (!zeton) {
    return <Vstop />;
  }
  return <>{children}</>;
}

/**
 * Skrbništvo sistema (samo skrbnik iz konfiguracije strežnika, s kodo TOTP): seznam instanc, nova instanca, nakazila,
 * lestvica bonusov, plačila (naš TRR, Stripe), e-pošta, umetna inteligenca, povratne informacije strank in dnevnik. Izbrani zavihek je v
 * URL-ju ({@code ?zavihek=}), da Nazaj iz podrobnosti vrne nanj.
 * Podrobnosti instance (urejanje, izklop, uporabniki, gesla, izbris) so v {@link InstancaSkrbnistvo}.
 */
export function Skrbnistvo() {
  return (
    <SkrbniskiZaslon>
      <SeznamInstanc />
    </SkrbniskiZaslon>
  );
}

function SeznamInstanc() {
  const prijavaKot = usePrijavaKot();
  const seznam = useSeznam<RazvrscanjeInstanc>();
  const [iskanje, setIskanje] = useState('');
  const [nova, setNova] = useState(false);
  const [parametri, setParametri] = useSearchParams();
  const zavihek = parametri.get('zavihek') ?? 'instance';
  const izberiZavihek = (v: string | null) =>
    setParametri(v && v !== 'instance' ? { zavihek: v } : {}, { replace: true });
  const nove = Number(useNovePovratne().data?.nove ?? 0);
  const noveZahteve = Number(useNoveZahteveAsistenta().data?.nove ?? 0);
  const instance = useQuery({
    queryKey: ['skrbnik', 'instance'],
    queryFn: async ({ signal }) => {
      try {
        return await podatki(api.GET('/api/skrbnik/instance', { signal, headers: glave() }));
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  const izstop = async () => {
    await api.POST('/api/skrbnik/izstop', { headers: glave() }).catch(() => undefined);
    skrbniskiZeton.pozabi();
  };
  const filter = iskanje.trim().toLowerCase();
  const vse = razvrstiInstance(instance.data ?? [], seznam.razvrsti, seznam.smer).filter(
    (i) =>
      !filter ||
      [i.naziv, i.domena, String(i.companyId)].some((x) =>
        String(x ?? '')
          .toLowerCase()
          .includes(filter),
      ),
  );

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Skrbništvo"
        opis="Instance (podjetja), njihovi uporabniki, povezava s Stripom, e-pošta, umetna inteligenca, asistent, povratne informacije strank in dnevnik skrbniških dejanj."
        dejanja={
          <Group gap="xs">
            <Button leftSection={<IconPlus size={18} />} onClick={() => setNova(true)}>
              Nova instanca
            </Button>
            <Button variant="default" leftSection={<IconLock size={18} />} onClick={() => void izstop()}>
              Zakleni
            </Button>
          </Group>
        }
      />
      <Tabs value={zavihek} onChange={izberiZavihek} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="instance">Instance</Tabs.Tab>
          <Tabs.Tab value="nakazila">Nakazila</Tabs.Tab>
          <Tabs.Tab value="bonusi">Bonusi</Tabs.Tab>
          <Tabs.Tab value="placila">Plačila</Tabs.Tab>
          <Tabs.Tab value="eposta">E-pošta</Tabs.Tab>
          <Tabs.Tab value="okroznice">Okrožnice</Tabs.Tab>
          <Tabs.Tab value="ai">Umetna inteligenca</Tabs.Tab>
          <Tabs.Tab
            value="asistent"
            rightSection={
              noveZahteve > 0 ? (
                <Badge size="sm" color="red" variant="filled" circle aria-label={`Nove zahteve: ${noveZahteve}`}>
                  {noveZahteve}
                </Badge>
              ) : undefined
            }
          >
            Asistent
          </Tabs.Tab>
          <Tabs.Tab
            value="povratne"
            rightSection={
              nove > 0 ? (
                <Badge size="sm" color="red" variant="filled" circle>
                  {nove}
                </Badge>
              ) : undefined
            }
          >
            Povratne informacije
          </Tabs.Tab>
          <Tabs.Tab value="dnevnik">Dnevnik</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="instance">
          <Kartica>
            <Group gap="xs" mb="sm" wrap="nowrap">
              <TextInput
                aria-label="Iskanje"
                placeholder="Naziv, domena ali številka"
                leftSection={<IconSearch size={16} />}
                value={iskanje}
                onChange={(e) => setIskanje(e.currentTarget.value)}
                maw={420}
                style={{ flex: 1 }}
              />
              {/* nove instance (registracija, drug zavihek) in sveži podatki brez izhoda iz skrbništva */}
              <Button
                variant="default"
                leftSection={<IconRefresh size={18} />}
                onClick={() => void instance.refetch()}
                loading={instance.isFetching}
              >
                Osveži
              </Button>
            </Group>
            {!instance.data ? (
              <StanjeNalaganja
                napaka={instance.error}
                ponovi={() => void instance.refetch()}
                naslov="Instanc ni bilo mogoče naložiti"
              />
            ) : (
              <Table.ScrollContainer minWidth={1180}>
                <Table highlightOnHover verticalSpacing={8}>
                  <Table.Thead>
                    <Table.Tr>
                      <Stolpec seznam={seznam} kljuc="naziv" naziv="Podjetje" />
                      <Stolpec seznam={seznam} kljuc="domena" naziv="Domena" />
                      <Stolpec seznam={seznam} kljuc="aktivna" naziv="Stanje" />
                      <Stolpec seznam={seznam} kljuc="narocnina" naziv="Naročnina" desno />
                      <Stolpec seznam={seznam} kljuc="placanoDo" naziv="Plačano do" />
                      <Stolpec seznam={seznam} kljuc="tocke" naziv="Točke" desno />
                      <Stolpec seznam={seznam} kljuc="uporabnikov" naziv="Uporabnikov" desno />
                      <Stolpec seznam={seznam} kljuc="zadnjaPrijava" naziv="Zadnja prijava" />
                      <Stolpec seznam={seznam} kljuc="aktivnihDni" naziv="Aktivnost (30 dni)" desno />
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {vse.map((i) => (
                      <Table.Tr key={i.companyId}>
                        <Table.Td>
                          <Anchor component={Link} to={`/skrbnistvo/${i.companyId}`} fw={600}>
                            {i.naziv}
                          </Anchor>
                          <Text size="xs" c="dimmed">
                            #{i.companyId} · {i.shema}
                          </Text>
                        </Table.Td>
                        <Table.Td>{i.domena}</Table.Td>
                        <Table.Td>
                          <StanjeInstance i={i} />
                        </Table.Td>
                        <Table.Td ta="right" className="brez-preloma">
                          <Text size="sm">{denar(i.narocnina)} €</Text>
                          <Text size="xs" c="dimmed">
                            {NAZIVI_PAKETOV[i.paket ?? 'PRODAJA']}
                            {i.posebnaNarocnina !== undefined && i.posebnaNarocnina !== null ? ' · posebna' : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td className="brez-preloma">
                          <ZnackaNarocnine stanje={i.stanjeNarocnine} />
                          <Text size="xs" c="dimmed">
                            {i.placanoDo ? datum(i.placanoDo) : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right" className="brez-preloma" c={(i.tocke ?? 0) < 0 ? 'red' : undefined}>
                          {tocke(i.tocke)}
                        </Table.Td>
                        <Table.Td ta="right">{stevilo(i.uporabnikov ?? 0)}</Table.Td>
                        <Table.Td>{cas(i.zadnjaPrijava)}</Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm">{i.aktivnihDni ? `${i.aktivnihDni} dni` : ''}</Text>
                          <Text size="xs" c="dimmed">
                            {cas(i.zadnjaAktivnost)}
                          </Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Button
                            size="xs"
                            variant="light"
                            leftSection={<IconLogin2 size={14} />}
                            disabled={!i.aktivna || !i.uporabnikov}
                            loading={prijavaKot.dela(i.companyId)}
                            title="Prijava kot uporabnik, ki se je nazadnje prijavil (pri uporabniku se ne zabeleži)"
                            onClick={() => void prijavaKot.prijavi(i.companyId!)}
                          >
                            Prijavi se
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Kartica>
        </Tabs.Panel>
        <Tabs.Panel value="nakazila">
          <Nakazila />
        </Tabs.Panel>
        <Tabs.Panel value="bonusi">
          <Bonusi />
        </Tabs.Panel>
        <Tabs.Panel value="placila">
          <PlacilaSkrbnistvo />
        </Tabs.Panel>
        <Tabs.Panel value="eposta">
          <EpostaSkrbnistvo />
        </Tabs.Panel>
        <Tabs.Panel value="okroznice">
          <OkroznicaSkrbnistvo />
        </Tabs.Panel>
        <Tabs.Panel value="ai">
          <UmetnaInteligencaSkrbnistvo />
        </Tabs.Panel>
        <Tabs.Panel value="asistent">
          <AsistentSkrbnistvo />
        </Tabs.Panel>
        <Tabs.Panel value="povratne">
          <PovratneSkrbnistvo />
        </Tabs.Panel>
        <Tabs.Panel value="dnevnik">
          <Dnevnik />
        </Tabs.Panel>
      </Tabs>
      <NovaInstanca odprto={nova} zapri={() => setNova(false)} />
    </ZaslonSeznama>
  );
}

const DEJANJA: Record<string, string> = {
  VSTOP: 'Vstop',
  VSTOP_NEUSPEL: 'Napačna koda',
  VSTOP_NEUSPEL_ZAKLEP: 'Napačna koda – zaklep',
  VSTOP_ZAKLENJEN: 'Vstop med zaklepom',
  NOVA_INSTANCA: 'Nova instanca',
  UREDI_INSTANCO: 'Urejanje instance',
  IZBRISI_INSTANCO: 'Izbris instance',
  DODAJ_UPORABNIKA: 'Nov uporabnik',
  PONASTAVI_GESLO: 'Ponastavitev gesla',
  PRIJAVA_KOT: 'Prijava kot uporabnik',
  OBRACUN: 'Naročnina – obračun',
  TOCKE: 'Naročnina – točke',
  BONUSI: 'Lestvica bonusov',
  SIMULACIJA_NAKAZILA: 'Simulirano nakazilo',
  NAKAZILO_TRR: 'Nakazilo na TRR',
  PLACILA: 'Nastavitve plačil',
  STRIPE_SHRANI: 'Stripe – ključi',
  STRIPE_ODKLOP: 'Stripe – odklop',
  EPOSTA: 'Nastavitve e-pošte',
  EPOSTA_PREIZKUS: 'Preizkusno sporočilo',
  OKROZNICA: 'Okrožnica',
  OKROZNICA_PREIZKUS: 'Okrožnica – preizkus',
  POVRATNA_ODGOVOR: 'Odgovor na povratno informacijo',
  POVRATNA_STANJE: 'Stanje povratne informacije',
  AI_SHRANI: 'Umetna inteligenca – povezava',
  AI_ODKLOP: 'Umetna inteligenca – odklop',
  AI_PREIZKUS: 'Umetna inteligenca – preizkus',
  AI_INSTANCA: 'Umetna inteligenca instance',
  AI_CENA: 'Umetna inteligenca – cenik',
  AI_NAVODILA: 'Umetna inteligenca – navodila',
};

function Dnevnik() {
  const dnevnik = useQuery({
    queryKey: ['skrbnik', 'dnevnik'],
    queryFn: async ({ signal }) => {
      try {
        return await podatki(api.GET('/api/skrbnik/dnevnik', { signal, headers: glave() }));
      } catch (e) {
        obNapaki(e);
        throw e;
      }
    },
  });
  return (
    <Kartica naslov="Zadnja dejanja">
      {!dnevnik.data ? (
        <StanjeNalaganja
          napaka={dnevnik.error}
          ponovi={() => void dnevnik.refetch()}
          naslov="Dnevnika ni bilo mogoče naložiti"
        />
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Čas</Table.Th>
                <Table.Th>Uporabnik</Table.Th>
                <Table.Th>Dejanje</Table.Th>
                <Table.Th>Instanca</Table.Th>
                <Table.Th>Podrobnosti</Table.Th>
                <Table.Th>Naslov</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {dnevnik.data.map((d, i) => (
                <Table.Tr key={i}>
                  <Table.Td className="brez-preloma">{cas(d.cas)}</Table.Td>
                  <Table.Td>{d.uporabnik}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c={d.dejanje?.startsWith('VSTOP_') ? 'red' : undefined} fw={500}>
                      {DEJANJA[d.dejanje ?? ''] ?? d.dejanje}
                    </Text>
                  </Table.Td>
                  <Table.Td>{d.companyId ? `#${d.companyId}` : ''}</Table.Td>
                  <Table.Td>{d.podrobnosti}</Table.Td>
                  <Table.Td>{d.naslov}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Kartica>
  );
}

const prazenUporabnik = { uporabniskoIme: '', ime: '', priimek: '', email: '' };

/**
 * Nova instanca: podjetje, domena, prvi uporabnik (začasno geslo se pokaže enkrat). Šifranti, predloge in nastavitve
 * DDV so začetni podatki strežnika.
 */
function NovaInstanca({ odprto, zapri }: { odprto: boolean; zapri: () => void }) {
  const odjemalec = useQueryClient();
  const navigate = useNavigate();
  const [naziv, setNaziv] = useState('');
  const [domena, setDomena] = useState('');
  const [domenaRocno, setDomenaRocno] = useState(false);
  const [prvi, setPrvi] = useState(prazenUporabnik);
  const [dodajMene, setDodajMene] = useState(true);
  const [paket, setPaket] = useState<Paket>('PRODAJA');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const [rezultat, setRezultat] = useState<Shema['NovaInstancaOdgovor'] | null>(null);

  const ponastavi = () => {
    setNaziv('');
    setDomena('');
    setDomenaRocno(false);
    setPrvi(prazenUporabnik);
    setDodajMene(true);
    setPaket('PRODAJA');
    setNapake({});
    setNapaka(null);
    setRezultat(null);
  };

  const ustvari = async () => {
    setDela(true);
    setNapake({});
    setNapaka(null);
    try {
      const o = await podatki(
        api.POST('/api/skrbnik/instance', {
          headers: glave(),
          body: {
            naziv,
            domena,
            prviUporabnik: { ...prvi, casSeje: 60 },
            dodajMene,
            paket,
          },
        }),
      );
      setRezultat(o);
      await odjemalec.invalidateQueries({ queryKey: ['skrbnik'] });
    } catch (e) {
      obNapaki(e);
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setDela(false);
    }
  };

  const koncaj = () => {
    const id = rezultat?.instanca?.companyId;
    ponastavi();
    zapri();
    if (id) {
      void navigate(`/skrbnistvo/${id}`);
    }
  };

  return (
    <Modal
      opened={odprto}
      onClose={() => (rezultat ? koncaj() : !dela && zapri())}
      title={rezultat ? 'Instanca je ustvarjena' : 'Nova instanca'}
      radius="lg"
      size="lg"
    >
      {rezultat ? (
        <ZacasnoGesloPrikaz
          opis={`Podjetje ${rezultat.instanca?.naziv} (${rezultat.instanca?.domena}) je pripravljeno. Prvi uporabnik se prijavi s tem začasnim geslom in ga ob prvi prijavi zamenja.`}
          uporabniskoIme={rezultat.uporabniskoIme}
          geslo={rezultat.zacasnoGeslo}
          zapri={koncaj}
        />
      ) : (
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label="Naziv podjetja"
              value={naziv}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setNaziv(v);
                if (!domenaRocno) {
                  setDomena(predlogDomene(v));
                }
              }}
              error={napake.naziv}
              data-autofocus
            />
            <TextInput
              label="Domena"
              description="Male črke, številke, vezaji"
              value={domena}
              onChange={(e) => {
                setDomena(e.currentTarget.value.toLowerCase());
                setDomenaRocno(true);
              }}
              error={napake.domena}
            />
          </SimpleGrid>
          <Select
            label="Paket"
            data={PAKETI.map((p) => ({ value: p, label: NAZIVI_PAKETOV[p] }))}
            value={paket}
            onChange={(v) => v && setPaket(v as Paket)}
            allowDeselect={false}
            error={napake.paket}
          />
          <Text size="sm" c="dimmed">
            Vrste vozil, predloge dokumentov, klavzule in nastavitve DDV dobi nova instanca iz začetnih podatkov.
          </Text>
          <Text size="sm" fw={600} mt="xs">
            Prvi uporabnik
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
              label="Uporabniško ime"
              value={prvi.uporabniskoIme}
              onChange={(e) => setPrvi({ ...prvi, uporabniskoIme: e.currentTarget.value })}
              error={napake.prviUporabnik}
            />
            <TextInput
              label="E-pošta"
              value={prvi.email}
              onChange={(e) => setPrvi({ ...prvi, email: e.currentTarget.value })}
            />
            <TextInput
              label="Ime"
              value={prvi.ime}
              onChange={(e) => setPrvi({ ...prvi, ime: e.currentTarget.value })}
            />
            <TextInput
              label="Priimek"
              value={prvi.priimek}
              onChange={(e) => setPrvi({ ...prvi, priimek: e.currentTarget.value })}
            />
          </SimpleGrid>
          <Checkbox
            label="Dodaj tudi mene (da instanco nastavim: podatki podjetja, logotip, FURS)"
            checked={dodajMene}
            onChange={(e) => setDodajMene(e.currentTarget.checked)}
          />
          {napaka && (
            <Alert color="red" variant="light" role="alert">
              {napaka}
            </Alert>
          )}
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={zapri} disabled={dela}>
              Prekliči
            </Button>
            <Button onClick={() => void ustvari()} loading={dela}>
              Ustvari instanco
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

/** Začasno geslo s kopiranjem; prikaže se samo enkrat (strežnik ga ne hrani). */
export function ZacasnoGesloPrikaz({
  opis,
  uporabniskoIme,
  geslo,
  zapri,
}: {
  opis: string;
  uporabniskoIme?: string;
  geslo?: string;
  zapri: () => void;
}) {
  return (
    <Stack gap="sm">
      <Text size="sm">{opis}</Text>
      <Kartica>
        <SimpleGrid cols={2} spacing="xs">
          <Text size="sm" c="dimmed">
            Uporabniško ime
          </Text>
          <Text size="sm" fw={600}>
            {uporabniskoIme}
          </Text>
          <Text size="sm" c="dimmed">
            Začasno geslo
          </Text>
          <Group gap="xs" wrap="nowrap">
            <Text size="lg" fw={700} ff="monospace" data-testid="zacasno-geslo">
              {geslo}
            </Text>
            <CopyButton value={geslo ?? ''}>
              {({ copied, copy }) => (
                <Button size="xs" variant="light" leftSection={<IconCopy size={14} />} onClick={copy}>
                  {copied ? 'Kopirano' : 'Kopiraj'}
                </Button>
              )}
            </CopyButton>
          </Group>
        </SimpleGrid>
      </Kartica>
      <Alert color="yellow" variant="light">
        Geslo se prikaže samo zdaj. Uporabniku ga sporočite po varni poti (npr. po telefonu), ne v isti e-pošti kot
        uporabniško ime.
      </Alert>
      <Group justify="flex-end">
        <Button onClick={zapri}>Zapri</Button>
      </Group>
    </Stack>
  );
}
